import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { 
  Volume2, 
  Play, 
  Pause, 
  Square, 
  Settings, 
  Keyboard, 
  MousePointer2, 
  ZoomIn,
  HelpCircle,
  X
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const AccessibilityToolbar: React.FC = () => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [clickToRead, setClickToRead] = useState(false);
  const [largeText, setLargeText] = useState(false);
  const [speechRate, setSpeechRate] = useState(1);
  const [speechSupported, setSpeechSupported] = useState(false);

  useEffect(() => {
    setSpeechSupported('speechSynthesis' in window);
  }, []);

  useEffect(() => {
    if (largeText) {
      document.documentElement.style.fontSize = '125%';
    } else {
      document.documentElement.style.fontSize = '';
    }
    return () => {
      document.documentElement.style.fontSize = '';
    };
  }, [largeText]);

  const getVoiceForLanguage = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    const langCode = language === 'nl' ? 'nl' : 'en';
    
    // Try to find a voice for the current language
    let voice = voices.find(v => v.lang.startsWith(langCode));
    
    // Fallback to any available voice
    if (!voice && voices.length > 0) {
      voice = voices[0];
    }
    
    return voice;
  }, [language]);

  const speakText = useCallback((text: string) => {
    if (!speechSupported) return;
    
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = speechRate;
    utterance.lang = language === 'nl' ? 'nl-NL' : 'en-US';
    
    const voice = getVoiceForLanguage();
    if (voice) {
      utterance.voice = voice;
    }
    
    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
    };
    
    window.speechSynthesis.speak(utterance);
    setIsPlaying(true);
    setIsPaused(false);
  }, [speechSupported, speechRate, language, getVoiceForLanguage]);

  const readPageContent = useCallback(() => {
    const mainContent = document.querySelector('main');
    if (mainContent) {
      const text = mainContent.innerText || mainContent.textContent || '';
      speakText(text);
    }
  }, [speakText]);

  const handlePlay = () => {
    if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    } else {
      readPageContent();
    }
  };

  const handlePause = () => {
    window.speechSynthesis.pause();
    setIsPaused(true);
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
  };

  useEffect(() => {
    if (!clickToRead) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.accessibility-toolbar')) return;
      
      const text = target.innerText || target.textContent || '';
      if (text.trim()) {
        speakText(text.trim());
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [clickToRead, speakText]);

  // Load voices when available
  useEffect(() => {
    if (!speechSupported) return;
    
    const loadVoices = () => {
      window.speechSynthesis.getVoices();
    };
    
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, [speechSupported]);

  if (!speechSupported) {
    return null;
  }

  const toolbarLabel = language === 'nl' ? 'Lees voor' : 'Read aloud';
  const settingsLabel = language === 'nl' ? 'Instellingen' : 'Settings';
  const clickToReadLabel = language === 'nl' ? 'Klik en lees voor' : 'Click and read';
  const largeTextLabel = language === 'nl' ? 'Vergroot tekst' : 'Enlarge text';
  const speedLabel = language === 'nl' ? 'Leessnelheid' : 'Reading speed';
  const helpLabel = language === 'nl' ? 'Help' : 'Help';
  const closeLabel = language === 'nl' ? 'Sluiten' : 'Close';

  return (
    <div className="accessibility-toolbar fixed bottom-4 left-4 z-50 flex items-center gap-1 bg-background border border-border rounded-lg shadow-lg p-1">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            aria-label={settingsLabel}
          >
            <Settings className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-80">
          <div className="flex flex-col gap-6 pt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{settingsLabel}</h2>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MousePointer2 className="h-5 w-5 text-muted-foreground" />
                <span>{clickToReadLabel}</span>
              </div>
              <Switch
                checked={clickToRead}
                onCheckedChange={setClickToRead}
                aria-label={clickToReadLabel}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ZoomIn className="h-5 w-5 text-muted-foreground" />
                <span>{largeTextLabel}</span>
              </div>
              <Switch
                checked={largeText}
                onCheckedChange={setLargeText}
                aria-label={largeTextLabel}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Volume2 className="h-5 w-5 text-muted-foreground" />
                <span>{speedLabel}</span>
              </div>
              <Slider
                value={[speechRate]}
                onValueChange={([value]) => setSpeechRate(value)}
                min={0.5}
                max={2}
                step={0.1}
                className="w-full"
                aria-label={speedLabel}
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>0.5x</span>
                <span>{speechRate.toFixed(1)}x</span>
                <span>2x</span>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <HelpCircle className="h-4 w-4" />
                <span>
                  {language === 'nl' 
                    ? 'Klik op de afspeelknop om de pagina voor te lezen, of schakel "Klik en lees voor" in om individuele elementen te selecteren.'
                    : 'Click the play button to read the page aloud, or enable "Click and read" to select individual elements.'}
                </span>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex items-center gap-1 px-2 border-l border-border">
        <Volume2 className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium px-2">{toolbarLabel}</span>
      </div>

      <div className="flex items-center gap-1 border-l border-border pl-1">
        {!isPlaying || isPaused ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-primary hover:text-primary"
            onClick={handlePlay}
            aria-label={language === 'nl' ? 'Afspelen' : 'Play'}
          >
            <Play className="h-5 w-5" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={handlePause}
            aria-label={language === 'nl' ? 'Pauzeren' : 'Pause'}
          >
            <Pause className="h-5 w-5" />
          </Button>
        )}
        
        {isPlaying && (
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={handleStop}
            aria-label={language === 'nl' ? 'Stoppen' : 'Stop'}
          >
            <Square className="h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default AccessibilityToolbar;
